import React, { useState, useEffect } from "react";
import { useRef } from "react";
import axios from "axios";
import { IoIosArrowDown } from "react-icons/io";
import { GrFormNext } from "react-icons/gr";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import capitalizeFirstLetter from "../../utils/capitalizeFirstLetter";
import NoCopyWrapper from "../../utils/NoCopyWrapper";
import NoZoomWrapper from "../../utils/NoZoomWrapper";
import clothImg from "../../assets/img/2.jpg";
import farmImg from "../../assets/img/3.jpg";
import vehiclesImg from "../../assets/img/4.jpg";
import eventsImg from "../../assets/img/Events.png";
import propertyImg from "../../assets/img/6.jpg";
import cameraImg from "../../assets/img/1.jpg";
import machineryImg from "../../assets/img/7.jpg";
import electronicsImg from "../../assets/img/8.jpg";
import clinicImg from "../../assets/img/9.jpg";
import driverImg from "../../assets/img/10.jpg";
import furnitureImg from "../../assets/img/11.jpg";
import cleaningImg from "../../assets/img/12.jpg";



const Manu = () => {
  const [categories, setCategories] = useState([]);
  const [allDropdownOpen, setAllDropdownOpen] = useState(false);
  const [nestedDropdown, setNestedDropdown] = useState(null);
  const [openHorizontalDropdown, setOpenHorizontalDropdown] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const hoverCloseTimer = useRef(null);
  const scrollRef = useRef(null);
  const scheduleDropdownClose = () => {
    if (hoverCloseTimer.current) clearTimeout(hoverCloseTimer.current);
    hoverCloseTimer.current = setTimeout(() => {
      setOpenHorizontalDropdown(null);
    }, 150);
  };

  const cancelDropdownClose = () => {
    if (hoverCloseTimer.current) {
      clearTimeout(hoverCloseTimer.current);
      hoverCloseTimer.current = null;
    }
  };

  // ✅ Drag-to-scroll functionality
  useEffect(() => {
    const slider = scrollRef.current;
    if (!slider) return;

    let isDown = false;
    let startX;
    let scrollLeft;

    const mouseDown = (e) => {
      isDown = true;
      slider.classList.add("active");
      startX = e.pageX - slider.offsetLeft;
      scrollLeft = slider.scrollLeft;
    };

    const mouseLeave = () => {
      isDown = false;
      slider.classList.remove("active");
    };

    const mouseUp = () => {
      isDown = false;
      slider.classList.remove("active");
    };

    const mouseMove = (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - slider.offsetLeft;
      const walk = (x - startX) * 1; // drag speed
      slider.scrollLeft = scrollLeft - walk;
    };

    // ✅ Mouse wheel horizontal scroll
    const mouseWheel = (e) => {
      if (e.deltaY !== 0) {
        e.preventDefault();
        slider.scrollLeft += e.deltaY; // vertical wheel → horizontal scroll
      }
    };

    slider.addEventListener("mousedown", mouseDown);
    slider.addEventListener("mouseleave", mouseLeave);
    slider.addEventListener("mouseup", mouseUp);
    slider.addEventListener("mousemove", mouseMove);
    slider.addEventListener("wheel", mouseWheel, { passive: false });

    return () => {
      slider.removeEventListener("mousedown", mouseDown);
      slider.removeEventListener("mouseleave", mouseLeave);
      slider.removeEventListener("mouseup", mouseUp);
      slider.removeEventListener("mousemove", mouseMove);
      slider.removeEventListener("wheel", mouseWheel);
    };
  }, []);


  useEffect(() => {
    return () => {
      if (hoverCloseTimer.current) clearTimeout(hoverCloseTimer.current);
    };
  }, []);

  // ✅ Responsive check
  useEffect(() => {
    const handleResize = () => {
      const newIsMobile = window.innerWidth <= 768;
      setIsMobile(newIsMobile);
      if (newIsMobile !== isMobile) {
        setAllDropdownOpen(false);
        setNestedDropdown(null);
        setOpenHorizontalDropdown(null);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isMobile]);

  // ✅ Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".category-container")) {
        setAllDropdownOpen(false);
        setNestedDropdown(null);
        setOpenHorizontalDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  // ✅ Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await axios.get("https://network.rentel.in/categories/get");
        const apiData = res.data.categories;

        const allCategory = {
          name: "All category",
          subCategories: apiData.map((cat) => ({
            id: cat._id,
            name: cat.name,
            subCategories: cat.subCategories,
          })),
        };

        const transformed = apiData.map((cat) => ({
          id: cat._id,
          name: cat.name,
          subCategories: cat.subCategories,
        }));

        setCategories([allCategory, ...transformed]);
      } catch (error) {
        console.error("Failed to fetch categories:", error);
      }
    };

    fetchCategories();
  }, []);

  const handleAllClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setAllDropdownOpen(!allDropdownOpen);
    setNestedDropdown(null);
    setOpenHorizontalDropdown(null);
  };

  const handleNestedClick = (name, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setNestedDropdown(nestedDropdown === name ? null : name);
  };

  const handleHorizontalClick = (categoryName) => {
    setSelectedCategory(categoryName);
    navigate(`/category/${categoryName.toLowerCase()}`);
    setAllDropdownOpen(false);
    setNestedDropdown(null);
    setOpenHorizontalDropdown(null);
  };

  const handleSubcategoryClick = (categoryName, subCategoryName) => {
    navigate(
      `/category/${categoryName.toLowerCase()}/${subCategoryName.toLowerCase()}`
    );
    setAllDropdownOpen(false);
    setNestedDropdown(null);
    setOpenHorizontalDropdown(null);
  };

  // ✅ Close dropdowns on scroll
  useEffect(() => {
    const handleScroll = () => {
      setAllDropdownOpen(false);
      setNestedDropdown(null);
      setOpenHorizontalDropdown(null);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleCategoryClick = (cat, e) => {
    e.preventDefault();
    e.stopPropagation();

    if (isMobile) {
      // 📱 Mobile behavior
      if (cat.subCategories.length > 0) {
        setOpenHorizontalDropdown(
          openHorizontalDropdown === cat.name ? null : cat.name
        );
        setSelectedCategory(
          openHorizontalDropdown === cat.name ? null : cat.name
        );
      } else {
        setSelectedCategory(cat.name);
        handleHorizontalClick(cat.name);
      }
    } else {
      // 💻 Desktop behavior
      if (cat.subCategories.length > 0) {
        if (openHorizontalDropdown === cat.name) {
          setOpenHorizontalDropdown(null);
          setSelectedCategory(null);
        } else {
          setSelectedCategory(cat.name);
          setOpenHorizontalDropdown(cat.name);
        }
      } else {
        setSelectedCategory(cat.name);
        handleHorizontalClick(cat.name);
      }
    }
  };

  // aa kam nu che ama img mukvni baki  che
  const categoryImages = {
    cloth: clothImg,
    farm: farmImg,
    vehicles: vehiclesImg,
    events: eventsImg,
    property: propertyImg,
    camera: cameraImg,
    machinery: machineryImg,
    electronics: electronicsImg,
    clinic: clinicImg,
    driver: driverImg,
    furniture: furnitureImg,
    cleaning: cleaningImg,
  };



  return (
    <NoZoomWrapper>
      <NoCopyWrapper>
        <div className="container">
          <div className="category-section">

            {/* HEADER */}
            <div className="category-headerds">
              <h3 className="category-heading">
                Top Rented Categories
              </h3>

              {/* <button
                className="all-category-btn"
                onClick={() => navigate("/categories")}
              >
                All Categories
              </button> */}
            </div>

            {/* GRID */}
            <div className="category-grid">
              {categories.slice(1).map((cat) => {
                const img =
                  categoryImages[cat.name.toLowerCase()] || "/placeholder.png";

                return (
                  <div
                    key={cat.name}
                    className="category-card"
                    onClick={() => {
                      const name = cat.name.trim().toLowerCase();
                      if (name === "cloth") {
                        navigate("/category/cloth/men", { state: { categoryId: cat.id } });
                      } else if (name === "vehicles") {
                        navigate("/category/vehicles/car",{ state: { categoryId: cat.id } });
                      } else if (name === "events") {
                        navigate("/category/events/dj & sound",{ state: { categoryId: cat.id } })
                      } else if (name === "machinery") {
                        navigate("/category/machinery/construction machinery",{ state: { categoryId: cat.id } });
                      } else {
                        navigate(`/category/${name}`,{ state: { categoryId: cat.id } });
                      }
                    }}
                  >
                    <div className="category-image-box">
                      <img
                        src={img}
                        alt={cat.name}
                        loading="lazy"
                      />
                    </div>

                    <p className="category-name">
                      {capitalizeFirstLetter(cat.name)}
                    </p>
                  </div>

                );
              })}
            </div>
          </div>
        </div>
      </NoCopyWrapper>
    </NoZoomWrapper >
  );

};

export default Manu;
